import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Login from './pages/Login';
import BrokerDashboard from './pages/BrokerDashboard';
import IntroducerDashboard from './pages/IntroducerDashboard';
import IntroducerReferralsPage from './pages/IntroducerReferralsPage';
import IntroducerCommissionsPage from './pages/IntroducerCommissionsPage';
import SubmitLead from './pages/SubmitLead';
import BrokerProfilePage from './pages/BrokerProfilePage';
import CreateIntroducer from './pages/CreateIntroducer';
import CreateDeal from './pages/CreateDeal';
import SignupPage from './pages/SignupPage';
import TeamInviteAccept from './pages/TeamInviteAccept';
import IntroducerTeamInviteAccept from './pages/IntroducerTeamInviteAccept';
import AgreementPage from './pages/AgreementPage';
import IntroducersPage from './pages/IntroducersPage';
import BrokerAnnouncementsPage from './pages/BrokerAnnouncementsPage';
import PendingReview from './pages/PendingReview';
import AgreementGate from './components/AgreementGate';
import CommissionDashboard from './pages/CommissionDashboard';
import DealDetails from './pages/DealDetails';
import IntroducerProfile from './pages/IntroducerProfile';
import CompanyDetails from './pages/CompanyDetails';
import UserManagement from './pages/UserManagement';
import OwnerAgreements from './pages/OwnerAgreements';
import AgreementStatusPage from './pages/AgreementStatusPage';
import OwnerDashboard from './pages/OwnerDashboard';
import OwnerCompanies from './pages/OwnerCompanies';
import OwnerUsers from './pages/OwnerUsers';
import OwnerAnalytics from './pages/OwnerAnalytics';
import OwnerIntroducers from './pages/OwnerIntroducers';
import OwnerCommissionAudit from './pages/OwnerCommissionAudit';
import OwnerFunnelAnalytics from './pages/OwnerFunnelAnalytics';
import OwnerClientDocuments from './pages/OwnerClientDocuments';
import OwnerEnquiries from './pages/OwnerEnquiries';
import OwnerAgreementTracker from './pages/OwnerAgreementTracker';
import OwnerAuditLog from './pages/OwnerAuditLog';
import OwnerRoleLabels from './pages/OwnerRoleLabels';
import OwnerOnboarding from './pages/OwnerOnboarding';
import ReferralsPage from './pages/ReferralsPage';
import ReferralConsentPage from './pages/ReferralConsentPage';
import LeadConsentPage from './pages/LeadConsentPage';
import PartnerPortal from './pages/PartnerPortal';
import PartnerSignup from './pages/PartnerSignup';
import OwnerPartners from './pages/OwnerPartners';
import OwnerProspects from './pages/OwnerProspects';
import OwnerProspectDetail from './pages/OwnerProspectDetail';
// FEEDBACK_WIDGET
import OwnerFeedback from './pages/OwnerFeedback';
// END FEEDBACK_WIDGET

import OwnerClients from './pages/OwnerClients';
import OwnerEmailTemplates from './pages/OwnerEmailTemplates';
import BrokerTeamManager from './components/BrokerTeamManager';
import TeamPage from './pages/TeamPage';
import TeamCalendarPage from './pages/TeamCalendarPage';
import BookingPage from './pages/BookingPage';
import AdminDocumentLibrary from './components/AdminDocumentLibrary';
import AdminEmailTemplates from './components/AdminEmailTemplates';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import AdminSettings from './components/AdminSettings';
import AdminUserManager from './components/AdminUserManager';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AnalyticsPage from './pages/AnalyticsPage';
import BillingPage from './pages/BillingPage';
import OwnerPipeline from './pages/OwnerPipeline';
import BillingBanner from './components/BillingBanner';
import IntroducerAnalyticsPage from './pages/IntroducerAnalyticsPage';
import IntroducerClientsPage from './pages/IntroducerClientsPage';
import IntroducerTeamManager from './components/IntroducerTeamManager';
import './App.css';
import LeadInbox from './pages/LeadInbox';
import DealsListPage from './pages/DealsListPage';
import ClientsPage from './pages/ClientsPage';
import BackBookPage from './pages/BackBookPage';
import DealsPage from './pages/DealsPage';
import StageManager from './pages/StageManager';
import Settings from './pages/Settings';
import DealEditPage from './pages/DealEditPage';
import ClientLogin from './pages/ClientLogin';
import ClientAcceptInvite from './pages/ClientAcceptInvite';
import ClientPortal from './pages/ClientPortal';
import EnquiriesPage from './pages/EnquiriesPage';
import LeadsPage from './pages/LeadsPage';
import BrokerAgreementPage from './pages/BrokerAgreementPage';
import AdminUsers from './pages/AdminUsers'
import WidgetPage from './pages/WidgetPage';
import ImpersonationBanner from './components/ImpersonationBanner';
import { useImpersonation } from './hooks/useImpersonation';
import { RoleLabelsProvider } from './contexts/RoleLabelsContext';
import ModuleGate from './components/ModuleGate';

// 3 Sept 2026 — redirect-after-login. Previously bounced straight to /login with no
// memory of where the visit was headed, so a deep link (e.g. the Back Book digest
// email's /backbook?client=<id> links) dropped its destination for anyone not already
// signed in. Captures the current path+query and hands it to Login.js via ?redirect=;
// Login.js is responsible for validating it's a safe same-origin path before using it
// (never trust this string further than that).
function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  const location = useLocation();
  if (token) return children;
  const dest = `${location.pathname}${location.search}`;
  const loginUrl = dest && dest !== '/' ? `/login?redirect=${encodeURIComponent(dest)}` : '/login';
  return <Navigate to={loginUrl} />;
}

function App() {
  const { impersonationData, exitImpersonation } = useImpersonation();
  return (
    <RoleLabelsProvider>
    <BrowserRouter>
      <BillingBanner />
      {impersonationData && (
        <ImpersonationBanner impersonationData={impersonationData} onExit={exitImpersonation} />
      )}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/introducer-login" element={<Navigate to="/login" replace />} />
        <Route path="/introducer-dashboard" element={<PrivateRoute><AgreementGate><IntroducerDashboard /></AgreementGate></PrivateRoute>} />
        <Route path="/introducer/referrals" element={<PrivateRoute><AgreementGate><IntroducerReferralsPage /></AgreementGate></PrivateRoute>} />
<Route path="/introducer/commissions" element={<PrivateRoute><AgreementGate><IntroducerCommissionsPage /></AgreementGate></PrivateRoute>} />
        <Route path="/submit-lead" element={<PrivateRoute><AgreementGate><SubmitLead /></AgreementGate></PrivateRoute>} />
        <Route path="/about-broker" element={<PrivateRoute><AgreementGate><BrokerProfilePage /></AgreementGate></PrivateRoute>} />
        
        {/* Owner Routes */}
        <Route path="/owner" element={<PrivateRoute><OwnerDashboard /></PrivateRoute>} />
        <Route path="/owner/companies" element={<PrivateRoute><OwnerCompanies /></PrivateRoute>} />
        <Route path="/owner/users" element={<PrivateRoute><OwnerUsers /></PrivateRoute>} />
              <Route path="/owner/agreements" element={<PrivateRoute><OwnerAgreements /></PrivateRoute>} />
              <Route path="/agreements-status" element={<PrivateRoute><AgreementStatusPage /></PrivateRoute>} />
        <Route path="/owner/analytics" element={<PrivateRoute><OwnerAnalytics /></PrivateRoute>} />
        <Route path="/owner/introducers" element={<PrivateRoute><OwnerIntroducers /></PrivateRoute>} />
        <Route path="/owner/commission-audit" element={<PrivateRoute><OwnerCommissionAudit /></PrivateRoute>} />
        <Route path="/owner/funnel-analytics" element={<PrivateRoute><OwnerFunnelAnalytics /></PrivateRoute>} />
        <Route path="/owner/client-documents" element={<PrivateRoute><OwnerClientDocuments /></PrivateRoute>} />
        <Route path="/owner/enquiries" element={<PrivateRoute><OwnerEnquiries /></PrivateRoute>} />
        <Route path="/owner/agreement-tracker" element={<PrivateRoute><OwnerAgreementTracker /></PrivateRoute>} />
        <Route path="/owner/audit-log" element={<PrivateRoute><OwnerAuditLog /></PrivateRoute>} />
        <Route path="/owner/role-labels" element={<PrivateRoute><OwnerRoleLabels /></PrivateRoute>} />
        <Route path="/owner/email-templates" element={<PrivateRoute><OwnerEmailTemplates /></PrivateRoute>} />
        <Route path="/owner/onboarding" element={<PrivateRoute><OwnerOnboarding /></PrivateRoute>} />
        <Route path="/owner/prospects" element={<PrivateRoute><OwnerProspects /></PrivateRoute>} />
        <Route path="/owner/prospects/:id" element={<PrivateRoute><OwnerProspectDetail /></PrivateRoute>} />
        <Route path="/owner/partners" element={<PrivateRoute><OwnerPartners /></PrivateRoute>} />
        <Route path="/referrals" element={<PrivateRoute><AgreementGate><ReferralsPage /></AgreementGate></PrivateRoute>} />
        {/* FEEDBACK_WIDGET */}
        <Route path="/owner/feedback" element={<PrivateRoute><OwnerFeedback /></PrivateRoute>} />
        {/* END FEEDBACK_WIDGET */}

        <Route path="/owner/clients" element={<PrivateRoute><OwnerClients /></PrivateRoute>} />
        <Route path="/owner/company/:companyId" element={<PrivateRoute><CompanyDetails /></PrivateRoute>} />
        
        {/* Broker Routes */}
        <Route path="/broker-agreement" element={<PrivateRoute><BrokerAgreementPage /></PrivateRoute>} />
        <Route path="/dashboard" element={<PrivateRoute><AgreementGate><BrokerDashboard /></AgreementGate></PrivateRoute>} />
        <Route path="/leads" element={<PrivateRoute><ModuleGate module="leads"><AgreementGate><LeadsPage /></AgreementGate></ModuleGate></PrivateRoute>} />
        <Route path="/enquiries" element={<PrivateRoute><AgreementGate><LeadsPage /></AgreementGate></PrivateRoute>} />
        <Route path="/deals" element={<PrivateRoute><ModuleGate module="deals"><AgreementGate><DealsPage /></AgreementGate></ModuleGate></PrivateRoute>} />
        <Route path="/clients" element={<PrivateRoute><ModuleGate module="clients"><AgreementGate><ClientsPage /></AgreementGate></ModuleGate></PrivateRoute>} />
        <Route path="/backbook" element={<PrivateRoute><AgreementGate><BackBookPage /></AgreementGate></PrivateRoute>} />
        <Route path="/stages" element={<PrivateRoute><StageManager /></PrivateRoute>} />
        <Route path="/admin/document-types" element={<PrivateRoute><AdminDocumentLibrary /></PrivateRoute>} />
        <Route path="/admin/email-templates" element={<PrivateRoute><EmailTemplatesPage /></PrivateRoute>} />
        <Route path="/admin/settings" element={<PrivateRoute><AdminSettings /></PrivateRoute>} />
        <Route path="/admin/users" element={<PrivateRoute><AdminUserManager /></PrivateRoute>} />
        <Route path="/admin/impersonate" element={<PrivateRoute><AdminUsers /></PrivateRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/admin/widget" element={<PrivateRoute><WidgetPage /></PrivateRoute>} />
        <Route path="/deal/:dealId/edit" element={<PrivateRoute><DealEditPage /></PrivateRoute>} />
        <Route path="/dashboard/team" element={<PrivateRoute><AgreementGate><BrokerTeamManager /></AgreementGate></PrivateRoute>} />
        <Route path="/team" element={<PrivateRoute><TeamPage /></PrivateRoute>} />
        <Route path="/team-calendar" element={<PrivateRoute><ModuleGate module="scheduling"><TeamCalendarPage /></ModuleGate></PrivateRoute>} />
        {/* 19 Aug 2026: BookingPageSettings.jsx folded into Settings.js's new
            'Calendar & Booking' tab - kept as a redirect rather than deleting the
            route outright, in case anything still links to it directly. */}
        <Route path="/booking-setup" element={<Navigate to="/settings?tab=calendar" replace />} />
        <Route path="/introducers" element={<PrivateRoute><ModuleGate module="introducers"><AgreementGate><IntroducersPage /></AgreementGate></ModuleGate></PrivateRoute>} />
        <Route path="/broker/announcements" element={<PrivateRoute><ModuleGate module="announcements"><AgreementGate><BrokerAnnouncementsPage /></AgreementGate></ModuleGate></PrivateRoute>} />
        <Route path="/analytics" element={<PrivateRoute><ModuleGate module="analytics"><AnalyticsPage /></ModuleGate></PrivateRoute>} />
        <Route path="/billing" element={<PrivateRoute><BillingPage /></PrivateRoute>} />
        <Route path="/owner/pipeline" element={<PrivateRoute><AgreementGate><DealsPage /></AgreementGate></PrivateRoute>} />
        <Route path="/introducer-analytics" element={<PrivateRoute><IntroducerAnalyticsPage /></PrivateRoute>} />
        <Route path="/introducer/clients" element={<PrivateRoute><AgreementGate><IntroducerClientsPage /></AgreementGate></PrivateRoute>} />
        <Route path="/dashboard/introducer-team" element={<PrivateRoute><IntroducerTeamManager /></PrivateRoute>} />
        <Route path="/commission-dashboard" element={<PrivateRoute><AgreementGate><DealsPage /></AgreementGate></PrivateRoute>} />
        <Route path="/deal/:dealId" element={<PrivateRoute><DealDetails /></PrivateRoute>} />
        <Route path="/introducer/user/:userId" element={<PrivateRoute><IntroducerProfile /></PrivateRoute>} />
        <Route path="/introducer/:introducerId" element={<PrivateRoute><IntroducerProfile /></PrivateRoute>} />
        <Route path="/company/:companyId" element={<PrivateRoute><CompanyDetails /></PrivateRoute>} />
        <Route path="/users/:companyId" element={<PrivateRoute><UserManagement /></PrivateRoute>} />
        <Route path="/create-introducer" element={<PrivateRoute><CreateIntroducer /></PrivateRoute>} />
        <Route path="/create-deal" element={<PrivateRoute><AgreementGate><CreateDeal /></AgreementGate></PrivateRoute>} />
        <Route path="/signup/:token" element={<SignupPage />} />
        <Route path="/team-invite/:token" element={<TeamInviteAccept />} />
        <Route path="/introducer-team-invite/:token" element={<IntroducerTeamInviteAccept />} />
        <Route path="/agreement/:token" element={<AgreementPage />} />
        <Route path="/book/:slug" element={<BookingPage />} />
        <Route path="/pending-review" element={<PendingReview />} />
        
        {/* Client Portal Routes */}
        <Route path="/client/login" element={<ClientLogin />} />
        <Route path="/client/accept-invite" element={<ClientAcceptInvite />} />
        <Route path="/client/portal" element={<ClientPortal />} />
        
        {/* Partner Portal */}
        <Route path="/partner/portal" element={<PrivateRoute><PartnerPortal /></PrivateRoute>} />

        {/* Referral GDPR Consent (public) */}
        <Route path="/referral-consent/:token" element={<ReferralConsentPage />} />
        <Route path="/lead-consent/:token" element={<LeadConsentPage />} />
        <Route path="/partner/signup/:token" element={<PartnerSignup />} />

      </Routes>
    </BrowserRouter>
    </RoleLabelsProvider>
  );
}

export default App;
